import { Modal } from "components";
import { EditSectionTemplate } from "components/MainSectionTemplate/EditSectionTemplate";
import { BookkeepingMultiModalPicker, ValidatedSelectedBookkeepings } from "components/Picker/Bookkeeping/BookkeepingMultiModalPicker";
import FreeFieldProfileModalPicker from "components/Picker/FreefieldProfile/FreefieldProfileModalPicker";
import React from "react";
import { UpgradeConfirmation } from "UserAdmin/components/UpgradeConfirmation";
import { useCustomQuery } from "utilities/hookutils";

interface Props {
  isOpen: boolean
  onClose: () => void
  userToClient: server.dto.UserToClientSmall
}

interface FormState {
  freeFieldProfile: server.dto.FreeFieldProfileSmall
  bookkeepings: server.dto.BookkeepingSmall[]
}

export const ReactivateUserForInternalModal = (props: Props) => {
  const [formState, setFormState] = React.useState<FormState>({
    freeFieldProfile: null,
    bookkeepings: [],
  });

  const maximumAccountsReachedApi = useCustomQuery({
    queryFn: async () => window.CoreApi.Subscription.CheckMaximumAccountsReached(),
    queryKey: ["maximumAccountsReached"],
    enabled: true,
  });

  const maximumAccountsReached = maximumAccountsReachedApi.data?.data?.data?.maximumReached || false;
  const upgradeMessage = maximumAccountsReachedApi.data?.data?.data?.upgradeMessage || null;

  const onChange = (newData: Partial<FormState>) => {
    setFormState((prevState) => ({
      ...prevState,
      ...newData,
    }));
  }

  const submit = () => window.CoreApi.UserForInternalAdmin.Reactivate({
    userToClientId: props.userToClient.userToClientId,
    freeFieldProfileId: formState.freeFieldProfile.freeFieldProfileId,
    bookkeepingIds: formState.bookkeepings.map(b => b.bookkeepingId),
    upgradeSubscription: maximumAccountsReached,
  })

  const onSuccess = () => {
    window.location.reload();
  }

  return <Modal width="tiny" title={RESX.SoUser.ReactivateAccount} handleClose={props.onClose} isOpen={props.isOpen}>
    <EditSectionTemplate
      submit={submit}
      saveButtonLabel={RESX.SoUser.ReactivateAccount}
      noPanels
      onSuccess={onSuccess}
      sections={[{
        key: "reactivateAccount",
        type: "regular",
        title: RESX.SoUser.ReactivateAccount,
        fields: [{
          key: "name",
          label: RESX.GeneralLabels.Name,
          renderEditNew: () => <div className="main-section-template-fields-padding">
            {props.userToClient.user.fullName}
          </div>
        }, {
          key: "freeFieldPermissionProfileLabel",
          label: RESX.SoUser.Permissions,
          hideLabel: true,
          renderEditNew: () => <div className="main-section-template-fields-padding">
            <h3>{RESX.SoUser.Permissions}</h3>
          </div>
        }, {
          key: "freeFieldPermissionProfile",
          label: RESX.FreeField.PermissionProfile,
          renderEditNew: () => <div className="main-section-template-fields-padding">
            <FreeFieldProfileModalPicker
              environment="internal"
              canCreateNew={false}
              nonInPageEditing={{
                onChange: v => onChange({ freeFieldProfile: v }),
                value: formState.freeFieldProfile,
                validatorKey: "freeFieldProfile",
                required: true,
              }}
            />
          </div>,
        }, {
          key: "bookkeepings",
          label: RESX.SoUser.AccessToBookKeepings,
          renderEditNew: () => <ValidatedSelectedBookkeepings
            value={formState.bookkeepings}
            validatorKey="bookkeepings"
          />,
          inPageEdit: {
            render: ({ cancel, submit }) => <BookkeepingMultiModalPicker
              inPageEditing={{ cancel }}
              value={formState.bookkeepings}
              onChange={bookkeepings => {
                onChange({ bookkeepings });
                submit();
              }}
              canCreateNew={false}
              required={true}
              bookkeepingSecurityScope="allForClient"
              validatorKey="bookkeepings"
            />,
            fullWidth: true,
          }
        }, {
          key: "upgradeSubscriptionLabel",
          label: RESX.Subscription.resxSubscription,
          hidden: !maximumAccountsReached,
          hideLabel: true,
          renderEditNew: () => <div className="main-section-template-fields-padding">
            <h3>{RESX.Subscription.resxSubscription}</h3>
          </div>
        }, {
          key: "upgradeSubscriptionCheck",
          label: RESX.Subscription.Upgrade,
          hidden: !maximumAccountsReached,
          renderEditNew: () => <UpgradeConfirmation
            upgradeMessage={upgradeMessage}
          />,
        }]
      }]}
    />
  </Modal>
}